<script setup>
// bubbles/BaileysInteractive.vue
// Renderiza mensagens interativas do WhatsApp App (Baileys):
// imagem + título + corpo + footer + botões de resposta
import { computed, ref } from 'vue';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';
import AudioChip from '../chips/Audio.vue';
import FileChip from '../chips/File.vue';

const { content, contentAttributes } = useMessageContext();

const copiedId = ref(null);
let copiedTimeout = null;

function onButtonClick(button) {
  if (button.type === 'url' && button.url) {
    window.open(button.url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (button.type !== 'copy' || !button.copyCode) return;
  navigator.clipboard.writeText(button.copyCode);
  copiedId.value = button.id || button.displayText;
  if (copiedTimeout) clearTimeout(copiedTimeout);
  copiedTimeout = setTimeout(() => { copiedId.value = null; }, 2000);
}

// MessageList aplica useCamelCase({ deep: true }), então as chaves internas
// de content_attributes chegam em camelCase: mediaUrl, mediaType, selectedButton
const title = computed(() => contentAttributes.value?.title || null);
const footer = computed(() => contentAttributes.value?.footer || null);
const buttons = computed(() => contentAttributes.value?.buttons || []);
const mediaUrl = computed(() => contentAttributes.value?.mediaUrl || null);
const mediaType = computed(() => contentAttributes.value?.mediaType || null);
// computed para reagir ao update via WebSocket quando o contato pressiona um botão
const selectedButton = computed(() => contentAttributes.value?.selectedButton || null);

const listButtons = computed(() => buttons.value.filter(b => b.type === 'list'));
const nonListButtons = computed(() => buttons.value.filter(b => b.type !== 'list'));

const isImage = computed(() => mediaType.value === 'image');
const isVideo = computed(() => mediaType.value === 'video');
const isAudio = computed(() => mediaType.value === 'audio');

// Objeto attachment compatível com os chips existentes (Audio, File)
const mediaAttachment = computed(() => {
  if (!mediaUrl.value) return null;
  return { dataUrl: mediaUrl.value };
});
</script>

<template>
  <BaseBubble class="px-4 py-3 min-w-[220px] overflow-hidden">
    <!-- Imagem: thumbnail 72×72 clicável que abre em nova aba -->
    <a
      v-if="mediaUrl && isImage"
      :href="mediaUrl"
      target="_blank"
      rel="noreferrer noopener"
      class="mb-3 size-[72px] overflow-hidden rounded-xl block relative group cursor-pointer flex-shrink-0"
    >
      <img :src="mediaUrl" class="object-cover w-full h-full" />
      <div
        class="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/35 transition-colors rounded-xl"
      >
        <span
          class="i-lucide-expand size-5 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow"
        />
      </div>
    </a>

    <!-- Vídeo: thumbnail com ícone de play, clique abre em nova aba -->
    <a
      v-else-if="mediaUrl && isVideo"
      :href="mediaUrl"
      target="_blank"
      rel="noreferrer noopener"
      class="mb-3 size-[72px] overflow-hidden rounded-xl block relative cursor-pointer flex-shrink-0"
    >
      <video :src="mediaUrl" class="w-full h-full object-cover" muted playsinline />
      <div class="absolute inset-0 flex items-center justify-center bg-black/30 rounded-xl">
        <div
          class="size-7 bg-white/60 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md"
        >
          <span class="i-teenyicons-play-small-solid size-7 text-slate-800" />
        </div>
      </div>
    </a>

    <!-- Áudio: player reutilizando chips/Audio.vue -->
    <AudioChip
      v-else-if="mediaUrl && isAudio && mediaAttachment"
      :attachment="mediaAttachment"
      class="mb-3"
    />

    <!-- Documento/PDF/outros: chip com ícone + nome + download (chips/File.vue) -->
    <FileChip
      v-else-if="mediaUrl && mediaAttachment"
      :attachment="mediaAttachment"
      class="mb-3"
    />

    <!-- Corpo -->
    <div class="flex flex-col gap-1.5">
      <p v-if="title" class="text-sm font-semibold leading-snug">{{ title }}</p>
      <p v-if="content" class="text-sm leading-relaxed whitespace-pre-wrap">{{ content }}</p>
      <p v-if="footer" class="text-xs opacity-70 mt-0.5">{{ footer }}</p>
    </div>

    <!-- Botões lado a lado (exceto list, que é renderizado separado) -->
    <div v-if="nonListButtons.length" class="flex gap-2 mt-3 flex-wrap">
      <button
        v-for="button in nonListButtons"
        :key="button.id || button.displayText"
        v-tooltip.bottom="
          button.type === 'copy' ? (button.copyCode || 'Copiar código')
          : button.type === 'url' ? (button.url || 'Abrir link')
          : ''
        "
        :class="[
          'flex flex-1 items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all select-none',
          (button.type === 'copy' || button.type === 'url') ? 'cursor-pointer' : 'cursor-default',
          copiedId === (button.id || button.displayText)
            ? 'bg-white/35 border-white/55'
            : selectedButton && (button.displayText || button.title) === selectedButton
              ? 'bg-white/35 border-white/55 shadow-[inset_0_2px_5px_rgba(0,0,0,0.3)] translate-y-px opacity-90'
              : 'border-white/30 bg-white/15 hover:bg-white/25 active:bg-white/10'
        ]"
        @click="onButtonClick(button)"
      >
        <span
          v-if="copiedId === (button.id || button.displayText)"
          class="i-lucide-check size-3.5 shrink-0"
        />
        <span
          v-else-if="selectedButton && (button.displayText || button.title) === selectedButton"
          class="i-lucide-check size-3.5 shrink-0"
        />
        <span v-else-if="button.type === 'url'" class="i-lucide-external-link size-3.5 shrink-0" />
        <span v-else-if="button.type === 'call'" class="i-lucide-phone size-3.5 shrink-0" />
        <span v-else-if="button.type === 'copy'" class="i-lucide-copy size-3.5 shrink-0" />
        <span class="truncate">
          {{ copiedId === (button.id || button.displayText) ? 'Copiado!' : (button.displayText || button.title) }}
        </span>
      </button>
    </div>

    <!-- Lista interativa -->
    <div
      v-for="listBtn in listButtons"
      :key="listBtn.id || 'list'"
      class="mt-3 border border-white/20 rounded-lg overflow-hidden"
    >
      <!-- Cabeçalho da lista -->
      <div class="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-sm font-medium">
        <span class="i-lucide-list size-3.5 shrink-0" />
        <span class="truncate">{{ listBtn.displayText || listBtn.title }}</span>
      </div>
      <!-- Seções e itens -->
      <div
        v-for="(section, sIdx) in listBtn.sections || []"
        :key="sIdx"
      >
        <div
          v-if="section.title"
          class="px-3 py-1 text-xs font-semibold opacity-70 bg-white/5"
        >
          {{ section.title }}
        </div>
        <div
          v-for="row in section.rows || []"
          :key="row.id || row.title"
          :class="[
            'flex items-center gap-2 px-3 py-1.5 border-t border-white/10',
            selectedButton && row.title === selectedButton
              ? 'bg-white/20'
              : ''
          ]"
        >
          <span
            v-if="selectedButton && row.title === selectedButton"
            class="i-lucide-check size-3 shrink-0 opacity-80"
          />
          <span v-else class="i-lucide-chevron-right size-3 shrink-0 opacity-40" />
          <div class="min-w-0">
            <p class="text-sm leading-tight truncate">{{ row.title }}</p>
            <p v-if="row.description" class="text-xs opacity-60 truncate">{{ row.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </BaseBubble>
</template>
